import { Texture } from 'pixi.js';

/**
 * 절차적 도트 이펙트 생성기.
 *
 * 스킬이 수십 개라 이펙트를 손으로 그릴 수 없다. 대신 코드가 찍는다.
 * 규칙은 셋뿐이다.
 *   1. 정수 격자에만 찍는다 (안티에일리어싱 금지)
 *   2. 속성마다 5단 색 램프만 쓴다 (심지 -> 가장자리)
 *   3. 확대는 최근접 (scaleMode nearest)
 *
 * 램프는 적소하 스프라이트 시트에서 뽑은 색을 기준으로 맞췄다.
 * 그래야 캐릭터와 이펙트가 같은 게임처럼 보인다.
 */

export type Attr = 'red' | 'blue' | 'yellow' | 'grey' | 'brown' | 'white' | 'steel';

/** 심지(밝음) -> 가장자리(어두움) 순서 */
export const RAMPS: Record<Attr, string[]> = {
  red: ['#fdf4e5', '#feee68', '#e11d28', '#aa0c24', '#5a0109'],
  blue: ['#ecfbff', '#9fe8ff', '#4cb8f0', '#2a6fc0', '#10305e'],
  yellow: ['#fffce0', '#ffe95c', '#ffc21f', '#d98a00', '#6b3b00'],
  grey: ['#f2fff8', '#c9f5e0', '#8ef0c0', '#4aa98a', '#1d4a3c'],
  brown: ['#f5e6cf', '#d2a86e', '#a8703c', '#6e441f', '#331c0c'],
  white: ['#ffffff', '#e8e8f5', '#b9b9d6', '#7a7a99', '#3a3a52'],
  steel: ['#fff6e0', '#ffd88a', '#e8e8f0', '#9aa0b5', '#3a3f52'],
};

export interface FxFrames {
  textures: Texture[];
  /** 스프라이트 anchor 로 그대로 넣을 값 (0~1) */
  anchorX: number;
  anchorY: number;
  /** 초당 프레임. 없으면 쓰는 쪽 기본값 */
  fps?: number;
  /** 불투명도 (없으면 1). 휘두르기(SwingFx)만 쓴다 */
  alpha?: number;
}

/** 다 쓴 프레임 묶음을 GPU 에서 내린다. 레벨업마다 다시 구우므로 안 지우면 샌다. */
export function destroyFrames(frames: FxFrames | null | undefined): void {
  if (!frames) return;
  for (const t of frames.textures) t.destroy(true);
}

// ── 픽셀 버퍼 ──────────────────────────────────────────────

export function hex(c: string): [number, number, number] {
  const n = parseInt(c.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export class PixelBuf {
  readonly data: Uint8ClampedArray;

  constructor(
    readonly w: number,
    readonly h: number,
  ) {
    this.data = new Uint8ClampedArray(w * h * 4);
  }

  set(x: number, y: number, rgb: [number, number, number]): void {
    if (x < 0 || y < 0 || x >= this.w || y >= this.h) return;
    const i = (y * this.w + x) * 4;
    this.data[i] = rgb[0];
    this.data[i + 1] = rgb[1];
    this.data[i + 2] = rgb[2];
    this.data[i + 3] = 255;
  }

  toTexture(): Texture {
    const canvas = document.createElement('canvas');
    canvas.width = this.w;
    canvas.height = this.h;
    const ctx = canvas.getContext('2d')!;
    const img = ctx.createImageData(this.w, this.h);
    img.data.set(this.data);
    ctx.putImageData(img, 0, 0);
    const tex = Texture.from(canvas);
    tex.source.scaleMode = 'nearest';
    tex.source.autoGenerateMipmaps = false;
    return tex;
  }
}

/** 프레임마다 같은 결과가 나오도록 씨앗 고정 난수를 쓴다. */
export function rand(seed: number): () => number {
  let s = seed >>> 0 || 1;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ── 반달 참격 ──────────────────────────────────────────────

interface CrescentOpts {
  /** 논리 픽셀 기준 최대 사거리 */
  radius: number;
  /** 부채꼴 각도(도) */
  arcDeg: number;
  ramp: string[];
  frames?: number;
}

/**
 * 반달 모양 참격. +x 방향을 향하며, 기준점(anchor)은 왼쪽 가운데 = 시전자 위치.
 * 프레임이 갈수록 바깥으로 퍼지고, 얇아지고, 흩어진다.
 */
export function crescent(opts: CrescentOpts): FxFrames {
  const { radius, arcDeg, ramp } = opts;
  const n = opts.frames ?? 5;
  const colors = ramp.map(hex);

  const pad = 3;
  const w = Math.ceil(radius) + pad * 2;
  const h = Math.ceil(radius * 2) + pad * 2;
  const cx = pad;
  const cy = h / 2;
  const span = (arcDeg * Math.PI) / 180;

  const textures: Texture[] = [];
  for (let f = 0; f < n; f++) {
    const t = n === 1 ? 0.35 : f / (n - 1); // 0 -> 1 로 진행
    const rmid = radius * (0.55 + 0.42 * t);
    const half = radius * (0.05 + 0.1 * Math.sin(Math.PI * Math.min(1, t * 1.15)));
    const sp = span * (0.82 + 0.28 * t);
    const sparse = Math.max(0, (t - 0.45) / 0.55) * 0.8;
    const rnd = rand(1337 + f * 97);

    const buf = new PixelBuf(w, h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const dx = x - cx;
        const dy = y - cy;
        const d = Math.hypot(dx, dy);
        if (d < 1) continue;
        const u = Math.abs(Math.atan2(dy, dx)) / (sp / 2);
        if (u > 1) continue;
        const taper = Math.sqrt(Math.max(0, 1 - u * u)); // 끝으로 갈수록 얇아진다
        const thick = half * taper;
        if (thick < 0.6) continue;
        const off = Math.abs(d - rmid);
        if (off > thick) continue;
        const k = off / thick; // 0 = 심지, 1 = 바깥
        if (sparse > 0 && rnd() < sparse * k) continue;
        buf.set(x, y, colors[Math.min(colors.length - 1, Math.floor(k * colors.length))]);
      }
    }
    textures.push(buf.toTexture());
  }
  return { textures, anchorX: cx / w, anchorY: 0.5 };
}

// ── 고리형 폭발 (오라 / 노바 / 장판) ──────────────────────

export function ring(radius: number, ramp: string[], frames = 5): FxFrames {
  const colors = ramp.map(hex);
  const pad = 3;
  const size = Math.ceil(radius * 2) + pad * 2;
  const c = size / 2;

  const textures: Texture[] = [];
  for (let f = 0; f < frames; f++) {
    const t = frames === 1 ? 0.5 : f / (frames - 1);
    const rmid = radius * (0.25 + 0.75 * t);
    const thick = radius * (0.22 - 0.14 * t);
    const sparse = Math.max(0, (t - 0.4) / 0.6) * 0.7;
    const rnd = rand(8081 + f * 131);

    const buf = new PixelBuf(size, size);
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const d = Math.hypot(x - c, y - c);
        const off = Math.abs(d - rmid);
        if (off > thick || thick < 0.5) continue;
        const k = off / thick;
        if (sparse > 0 && rnd() < sparse * k) continue;
        buf.set(x, y, colors[Math.min(colors.length - 1, Math.floor(k * colors.length))]);
      }
    }
    textures.push(buf.toTexture());
  }
  return { textures, anchorX: 0.5, anchorY: 0.5 };
}

// ── 탄환용 작은 조각들 ────────────────────────────────────

export type BoltShape = 'ember' | 'shard' | 'spark' | 'chunk' | 'gust' | 'orb';

/**
 * 탄환/룬 한 장짜리 텍스처. +x 를 향한다.
 * size 는 반지름(논리 픽셀).
 */
export function bolt(shape: BoltShape, size: number, ramp: string[], seed = 0): Texture {
  const colors = ramp.map(hex);
  const r = Math.max(2, Math.round(size));
  const w = r * 4;
  const h = r * 2 + 2;
  const cx = r * 2;
  const cy = h / 2;
  const buf = new PixelBuf(w, h);
  const rnd = rand(4242 + r * 31 + shape.length + seed * 977);

  const put = (x: number, y: number, k: number): void =>
    buf.set(Math.round(x), Math.round(y), colors[Math.min(colors.length - 1, Math.max(0, Math.floor(k * colors.length)))]);

  switch (shape) {
    case 'orb':
    case 'ember': {
      // 앞이 뭉툭하고 뒤로 꼬리가 늘어지는 덩어리
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const dx = (x - cx) / (shape === 'ember' ? 1.9 : 1.0);
          const dy = y - cy;
          const d = Math.hypot(dx, dy);
          if (d > r) continue;
          let k = d / r;
          if (shape === 'ember' && x < cx) k = Math.min(1, k + rnd() * 0.4); // 꼬리는 성기게
          if (shape === 'ember' && x < cx - r && rnd() < 0.5) continue;
          put(x, y, k);
        }
      }
      break;
    }
    case 'shard': {
      // 뾰족한 마름모
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const dx = (x - cx) / (r * 1.8);
          const dy = (y - cy) / r;
          const m = Math.abs(dx) + Math.abs(dy);
          if (m > 1) continue;
          put(x, y, m);
        }
      }
      break;
    }
    case 'spark': {
      // 지그재그 번개
      let y = cy;
      for (let x = 1; x < w - 1; x++) {
        y += (rnd() - 0.5) * r * 1.1;
        y = Math.max(1, Math.min(h - 2, y));
        put(x, y, 0);
        put(x, y - 1, 0.5);
        put(x, y + 1, 0.5);
        if (rnd() < 0.3) put(x, y + (rnd() < 0.5 ? -2 : 2), 0.9);
      }
      break;
    }
    case 'chunk': {
      // 각진 돌조각
      const verts = 7;
      const rs: number[] = [];
      for (let i = 0; i < verts; i++) rs.push(r * (0.65 + rnd() * 0.35));
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const dx = x - cx;
          const dy = y - cy;
          const d = Math.hypot(dx, dy);
          const a = (Math.atan2(dy, dx) + Math.PI) / (Math.PI * 2);
          const rr = rs[Math.floor(a * verts) % verts];
          if (d > rr) continue;
          put(x, y, d / rr);
        }
      }
      break;
    }
    case 'gust': {
      // 가로로 길게 흐르는 바람결 세 줄
      for (let i = 0; i < 3; i++) {
        const oy = cy + (i - 1) * Math.max(1, r * 0.7);
        const len = w * (0.55 + rnd() * 0.45);
        const x0 = (w - len) / 2;
        for (let x = 0; x < len; x++) {
          const p = x / len;
          if (rnd() < 0.15) continue;
          put(x0 + x, oy + Math.sin(p * Math.PI) * (i - 1) * 0.6, i === 1 ? p * 0.6 : 0.4 + p * 0.6);
        }
      }
      break;
    }
  }
  return buf.toTexture();
}

/** 경험치 보석처럼 작고 또렷한 도트 하나. */
export function gemTexture(size: number, ramp: string[]): Texture {
  const colors = ramp.map(hex);
  const r = Math.max(2, Math.round(size));
  const s = r * 2 + 1;
  const c = r;
  const buf = new PixelBuf(s, s);
  for (let y = 0; y < s; y++) {
    for (let x = 0; x < s; x++) {
      const m = Math.abs(x - c) / r + Math.abs(y - c) / r;
      if (m > 1) continue;
      buf.set(x, y, colors[Math.min(colors.length - 1, Math.floor(m * colors.length))]);
    }
  }
  return buf.toTexture();
}

// ── 적·보석용 ─────────────────────────────────────────────

/** 색 하나에서 5단 램프를 만든다. 적 종류마다 고유 색을 쓰기 위한 것. */
export function rampFromColor(base: string): string[] {
  const [r, g, b] = hex(base);
  const mix = (t: number): string => {
    // t > 0 이면 흰색 쪽, t < 0 이면 검정 쪽으로
    const f = (v: number): number =>
      Math.round(t >= 0 ? v + (255 - v) * t : v * (1 + t));
    return `#${[f(r), f(g), f(b)].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
  };
  return [mix(0.75), mix(0.35), base, mix(-0.35), mix(-0.65)];
}

/**
 * 적 몸통. 각진 덩어리에 위쪽이 밝은 음영을 준다.
 * seed 를 바꾸면 실루엣이 달라진다(종류별로 다르게 보이도록).
 */
export function blob(radius: number, ramp: string[], seed = 1): Texture {
  const colors = ramp.map(hex);
  const r = Math.max(3, Math.round(radius));
  const s = r * 2 + 2;
  const c = s / 2;
  const buf = new PixelBuf(s, s);
  const rnd = rand(seed * 7919 + r);

  const verts = 9;
  const rs: number[] = [];
  for (let i = 0; i < verts; i++) rs.push(r * (0.78 + rnd() * 0.22));

  for (let y = 0; y < s; y++) {
    for (let x = 0; x < s; x++) {
      const dx = x - c;
      const dy = y - c;
      const d = Math.hypot(dx, dy);
      const a = (Math.atan2(dy, dx) + Math.PI) / (Math.PI * 2);
      const i0 = Math.floor(a * verts) % verts;
      const i1 = (i0 + 1) % verts;
      const f = a * verts - Math.floor(a * verts);
      const rr = rs[i0] * (1 - f) + rs[i1] * f;
      if (d > rr) continue;
      if (d > rr - 1.2) {
        buf.set(x, y, colors[4]); // 외곽선
        continue;
      }
      // 위쪽이 밝고 아래쪽이 어둡다
      const shade = 0.5 + (dy / r) * 0.55 + (dx / r) * 0.12;
      buf.set(x, y, colors[Math.min(3, Math.max(0, Math.round(shade * 3.2)))]);
    }
  }
  return buf.toTexture();
}

/**
 * 바닥 장판. 안쪽은 격자무늬로 성글게 채우고 테두리는 또렷하게 둘러
 * 반투명 알파 대신 도트 자체로 "비쳐 보이는" 느낌을 만든다.
 */
export function fieldTexture(radius: number, ramp: string[]): Texture {
  const colors = ramp.map(hex);
  const r = Math.max(4, Math.round(radius));
  const s = r * 2 + 2;
  const c = s / 2;
  const buf = new PixelBuf(s, s);
  for (let y = 0; y < s; y++) {
    for (let x = 0; x < s; x++) {
      const d = Math.hypot(x - c, y - c);
      if (d > r) continue;
      if (d > r - 2) {
        buf.set(x, y, colors[1]); // 테두리
        continue;
      }
      // 체크무늬로 절반만 찍는다 = 반투명처럼 보인다
      if (((x + y) & 1) === 0) continue;
      const k = d / r;
      buf.set(x, y, colors[k > 0.6 ? 3 : 2]);
    }
  }
  return buf.toTexture();
}

// ── 맵에 떨어지는 픽업 ────────────────────────────────────

export type PickupKind = 'heal' | 'xp' | 'xpBig' | 'xpHuge';

/**
 * 멀리서도 "저건 주워야 한다"가 바로 읽히도록 만든다.
 * 흰 심지 + 굵은 테두리 + 또렷한 실루엣. 배경과 헷갈리면 안 된다.
 */
export function pickupTexture(kind: PickupKind, size: number, ramp: string[]): Texture {
  const colors = ramp.map(hex);
  const white = hex('#ffffff');
  const r = Math.max(4, Math.round(size));
  const s = r * 2 + 3;
  const c = s / 2;
  const buf = new PixelBuf(s, s);

  const put = (x: number, y: number, rgb: [number, number, number]): void =>
    buf.set(Math.round(x), Math.round(y), rgb);

  if (kind === 'heal') {
    // 두꺼운 십자. 한가운데는 하얗게 빛난다.
    const t = Math.max(1, Math.round(r * 0.36));
    for (let y = 0; y < s; y++) {
      for (let x = 0; x < s; x++) {
        const dx = Math.abs(x - c);
        const dy = Math.abs(y - c);
        const inside = (dx <= t && dy <= r) || (dy <= t && dx <= r);
        if (!inside) continue;
        const edge = dx > r - 1.2 || dy > r - 1.2 || (dx > t - 1.2 && dy > t - 1.2);
        const core = dx <= t * 0.45 && dy <= t * 0.45;
        put(x, y, core ? white : edge ? colors[4] : dx + dy < r * 0.7 ? colors[0] : colors[2]);
      }
    }
    return buf.toTexture();
  }

  // 경험치: 마름모(작은 것) / 뾰족한 별(큰 것)
  const points = kind === 'xp' ? 4 : kind === 'xpBig' ? 5 : 6;
  for (let y = 0; y < s; y++) {
    for (let x = 0; x < s; x++) {
      const dx = x - c;
      const dy = y - c;
      const d = Math.hypot(dx, dy);
      if (d < 0.5) {
        put(x, y, white);
        continue;
      }
      const a = Math.atan2(dy, dx);
      const spike = 0.58 + 0.42 * Math.abs(Math.cos(a * (points / 2)));
      const rr = r * spike;
      if (d > rr) continue;
      const k = d / rr;
      put(x, y, k > 0.86 ? colors[4] : k < 0.3 ? white : colors[k < 0.62 ? 1 : 2]);
    }
  }
  return buf.toTexture();
}

// ── 적 실루엣 ─────────────────────────────────────────────

/**
 * 적은 "무슨 짓을 하는 놈인지"가 실루엣만 보고 읽혀야 한다.
 * 색만 다르면 전부 같은 덩어리로 보인다. 그래서 모양을 따로 만든다.
 */
export type EnemyShape = 'blob' | 'spike' | 'wisp' | 'armored' | 'sac' | 'bulb' | 'eye';

export function enemySprite(shape: EnemyShape, radius: number, ramp: string[], seed = 1): Texture {
  const colors = ramp.map(hex);
  const white = hex('#ffffff');
  const r = Math.max(4, Math.round(radius));
  const s = r * 2 + 3;
  const c = s / 2;
  const buf = new PixelBuf(s, s);
  const rnd = rand(seed * 7919 + r);

  // 실루엣: 각도 -> 반지름
  const N = 24;
  const rs: number[] = [];
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2;
    let v = 1;
    switch (shape) {
      case 'blob':
        v = 0.82 + rnd() * 0.18;
        break;
      case 'spike':
        // 뾰족한 돌기 여섯 — 빠르고 공격적인 놈
        v = 0.55 + 0.45 * Math.abs(Math.cos(a * 3));
        break;
      case 'wisp':
        // 위는 좁고 아래로 꼬리가 늘어진다
        v = 0.95 - 0.45 * Math.cos(a) * Math.cos(a) * (Math.sin(a) > 0 ? 0.2 : 1);
        break;
      case 'armored':
        // 육각 판金 — 단단해 보이게
        v = 0.86 / Math.max(0.55, Math.cos(((a % (Math.PI / 3)) - Math.PI / 6) * 1.0));
        v = Math.min(1, v);
        break;
      case 'sac':
        v = 0.9 + 0.1 * Math.sin(a * 2);
        break;
      case 'bulb':
        // 부풀어 오른 물주머니 — 터질 것 같은 모양
        v = 0.78 + 0.22 * Math.sin(a + 1.2) + 0.08 * Math.sin(a * 4);
        break;
      case 'eye':
        v = 0.95;
        break;
    }
    rs.push(r * Math.max(0.35, Math.min(1, v)));
  }

  for (let y = 0; y < s; y++) {
    for (let x = 0; x < s; x++) {
      const dx = x - c;
      const dy = y - c;
      const d = Math.hypot(dx, dy);
      const a = (Math.atan2(dy, dx) + Math.PI) / (Math.PI * 2);
      const i0 = Math.floor(a * N) % N;
      const i1 = (i0 + 1) % N;
      const f = a * N - Math.floor(a * N);
      const rr = rs[i0] * (1 - f) + rs[i1] * f;
      if (d > rr) continue;

      // 외곽선은 항상 가장 어두운 색 — 겹쳐 있어도 개체가 구분된다
      if (d > rr - (shape === 'armored' ? 2.2 : 1.2)) {
        buf.set(x, y, colors[4]);
        continue;
      }
      const shade = 0.5 + (dy / r) * 0.55 + (dx / r) * 0.12;
      buf.set(x, y, colors[Math.min(3, Math.max(0, Math.round(shade * 3.2)))]);
    }
  }

  // 종류별 표식 — 멀리서도 뭔지 알아보게
  if (shape === 'sac') {
    // 안에 알이 들어 있다 (쪼개지는 놈)
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI * 2 + 0.4;
      const px = c + Math.cos(a) * r * 0.42;
      const py = c + Math.sin(a) * r * 0.42;
      for (let yy = -1; yy <= 1; yy++) {
        for (let xx = -1; xx <= 1; xx++) buf.set(Math.round(px + xx), Math.round(py + yy), colors[0]);
      }
    }
  } else if (shape === 'bulb') {
    // 한가운데가 하얗게 달아올라 있다 (터지는 놈)
    const k = Math.max(1, Math.round(r * 0.3));
    for (let yy = -k; yy <= k; yy++) {
      for (let xx = -k; xx <= k; xx++) {
        if (xx * xx + yy * yy > k * k) continue;
        buf.set(Math.round(c + xx), Math.round(c + yy), xx * xx + yy * yy < k * k * 0.3 ? white : colors[0]);
      }
    }
  } else if (shape === 'eye') {
    // 노려보는 눈 하나 (원거리형)
    const k = Math.max(2, Math.round(r * 0.42));
    for (let yy = -k; yy <= k; yy++) {
      for (let xx = -k; xx <= k; xx++) {
        if (xx * xx + yy * yy > k * k) continue;
        buf.set(Math.round(c + xx), Math.round(c + yy), white);
      }
    }
    const p = Math.max(1, Math.round(r * 0.18));
    for (let yy = -p; yy <= p; yy++) {
      for (let xx = -p; xx <= p; xx++) buf.set(Math.round(c + xx), Math.round(c + yy), colors[4]);
    }
  }
  return buf.toTexture();
}
